// src/screens/admin/CotizadorCotizacion.jsx
// Pantalla principal del Cotizador: cabecera de la cotización, mejor opción
// y una fila por día de vuelo. Al tocar una fila se abre el detalle editable
// con el desglose completo. Todo se guarda solo.
import React, { useState, useEffect, useMemo } from 'react';
import { toast } from '../../components/UI';
import {
  obtenerCotizacion, actualizarCotizacion, actualizarOpcion, agregarOpcion, borrarOpcion, moverFilasPorLlegada,
  listarRevendedores, crearRevendedor, actualizarRevendedor, hoyIso, duplicarOpcion, crearOpcionesDespues,
} from '../../services/cotizadorService';
import {
  calcularOpcion, mejorOpcion, fmtBRL, fmtFecha, fmtHoraAbs, sumarDias, diaNum, masDiasLlegada, duracionVueloMin, fmtDuracion, esNum,
} from '../../services/cotizadorCalc';
import {
  Campo, NumInput, inputStyle, cardStyle, btnChico, ESTADOS, diaSemana, useAutoGuardado, IndicadorGuardado, resumenFila,
} from './CotizadorUI';
import CotizadorImagen from './CotizadorImagen';
import CotizadorOrcamento from './CotizadorOrcamento';
import CotizadorCapturas from './CotizadorCapturas';
import { aeropuertoSalidaDe, aeropuertoDeCiudad, aerolineaDeVuelo, AEROLINEAS_COMUNES } from '../../services/aeropuertos';
import { cargarRegistros } from './cotizadorRegistros';
import { lineaReferencia, normalizarCiudad } from '../../services/cotizadorHistorial';

const fmtEspera = (min) => {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
};

function Linea({ k, v, fuerte, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '4px 0', fontSize: 13 }}>
      <span style={{ color: 'var(--muted)' }}>{k}</span>
      <span style={{ color: color || 'var(--white)', fontWeight: fuerte ? 800 : 600, textAlign: 'right' }}>{v}</span>
    </div>
  );
}

function ExtraOmnibus({ extra, indice, tramo, onChange, onBorrar }) {
  const set = (patch) => onChange({ ...extra, ...patch });
  let aviso = null;
  if (tramo.ok === false) {
    aviso = { color: 'var(--error)', texto: `✕ No sirve: sale antes del margen. Búscalo desde ${fmtHoraAbs(tramo.desde)}.` };
  } else if (tramo.ok === true) {
    aviso = { color: 'var(--success)', texto: `✓ Sirve. Espera ${fmtEspera(tramo.esperaMin)} en rodoviaria/aeropuerto.` };
  } else if (tramo.desde !== null) {
    aviso = { color: 'var(--gold)', texto: `Puedes tomarlo desde ${fmtHoraAbs(tramo.desde)}.` };
  }
  return (
    <div style={{ border: '1px dashed var(--navy-border)', borderRadius: 10, padding: 10, marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--orange)' }}>Ómnibus extra {indice + 1}</span>
        <button onClick={onBorrar} style={{ ...btnChico, padding: '4px 10px', color: 'var(--error)' }}>Quitar</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <Campo label="Ciudad / ruta">
          <input value={extra.ciudad || ''} onChange={(e) => set({ ciudad: e.target.value })} placeholder="Curitiba → Cascavel" style={inputStyle} />
        </Campo>
        <Campo label="Total BRT (R$)">
          <NumInput value={extra.total} onChange={(v) => set({ total: v })} />
        </Campo>
        <Campo label="Sale (día)">
          <input type="date" value={extra.sale_fecha || ''} onChange={(e) => set({ sale_fecha: e.target.value })} style={inputStyle} />
        </Campo>
        <Campo label="Sale (hora)">
          <input type="time" value={extra.sale_hora || ''} onChange={(e) => set({ sale_hora: e.target.value })} style={inputStyle} />
        </Campo>
        <Campo label="Llega (día)">
          <input type="date" value={extra.llega_fecha || ''} onChange={(e) => set({ llega_fecha: e.target.value })} style={inputStyle} />
        </Campo>
        <Campo label="Llega (hora)">
          <input type="time" value={extra.llega_hora || ''} onChange={(e) => set({ llega_hora: e.target.value })} style={inputStyle} />
        </Campo>
        <Campo label="Empresa (Detallado)"><input value={extra.empresa || ''} onChange={(e) => set({ empresa: e.target.value })} style={inputStyle} /></Campo>
        <Campo label="Terminal de salida"><input value={extra.terminal_origen || ''} onChange={(e) => set({ terminal_origen: e.target.value })} style={inputStyle} /></Campo>
        <Campo label="Terminal de llegada"><input value={extra.terminal_destino || ''} onChange={(e) => set({ terminal_destino: e.target.value })} style={inputStyle} /></Campo>
      </div>
      {aviso && <p style={{ fontSize: 12, color: aviso.color, marginTop: 8, lineHeight: 1.5 }}>{aviso.texto}</p>}
    </div>
  );
}

function Detalle({ op, r, ajustes, referencia, onChange, onBorrar, onDuplicar, onCapturas }) {
  const set = (patch) => onChange(patch);
  const extras = op.extras || [];
  const setExtra = (i, nuevo) => set({ extras: extras.map((e, j) => (j === i ? nuevo : e)) });
  const mejorIdx = r.mejorFuente ? ['Comprarviagem', 'Speedmilhas', 'Resumen del pedido', 'Millas'].indexOf(r.mejorFuente) : -1;
  const nombresFuentes = ['Comprarviagem (lista × factor)', 'Speedmilhas (Total Pix)', 'Resumen del pedido', 'Millas'];
  const est = r.estado ? ESTADOS[r.estado] : null;
  const esRev = r.tipo === 'revendedor';

  return (
    <div style={{ padding: '4px 2px 6px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
        <CotizadorCapturas op={op} onAplicar={onCapturas} />
        <button onClick={onDuplicar} style={{ ...btnChico, color: 'var(--orange)', borderColor: 'var(--orange)', fontWeight: 700 }}>
          ＋ Otro vuelo este día
        </button>
      </div>
      <p className="section-label" style={{ marginTop: 12 }}>VUELO</p>
      <Campo label="Sale de" style={{ marginBottom: 8 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {[['MAO', 'Manaus (con ómnibus)'], ['BVB', 'Boa Vista (en avión)']].map(([v, t]) => (
            <button key={v} type="button" onClick={() => set({ sale_de: v })} style={{
              ...btnChico, flex: 1, fontWeight: 700,
              color: (op.sale_de === 'BVB' ? 'BVB' : 'MAO') === v ? 'var(--orange)' : 'var(--muted)',
              borderColor: (op.sale_de === 'BVB' ? 'BVB' : 'MAO') === v ? 'var(--orange)' : 'var(--navy-border)',
            }}>{t}</button>
          ))}
        </div>
      </Campo>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
        <Campo label="Día del vuelo">
          <input type="date" value={op.dia_vuelo || ''} onChange={(e) => set({ dia_vuelo: e.target.value })} style={inputStyle} />
        </Campo>
        <Campo label={`Hora salida de ${op.sale_de === 'BVB' ? 'Boa Vista' : 'Manaus'}`}>
          <input type="time" value={op.hora_vuelo || ''} onChange={(e) => set({ hora_vuelo: e.target.value })} style={inputStyle} />
        </Campo>
        <Campo label="Hora llegada al destino">
          <input type="time" value={op.hora_llegada_vuelo || ''} onChange={(e) => set({ hora_llegada_vuelo: e.target.value })} style={inputStyle} />
        </Campo>
        <Campo label="Llega">
          <select
            value={esNum(op.llegada_mas_dias) ? String(op.llegada_mas_dias) : ''}
            onChange={(e) => set({ llegada_mas_dias: e.target.value })} style={inputStyle}
          >
            <option value="">Automático ({masDiasLlegada(op, ajustes.hora_vuelo_defecto) > 0 ? 'día siguiente' : 'mismo día'})</option>
            <option value="0">El mismo día</option>
            <option value="1">+1 día (día siguiente)</option>
            <option value="2">+2 días</option>
          </select>
        </Campo>
        <Campo label="Ciudad (opcional)">
          <input list="cotizador-ciudades" value={op.ciudad || ''} onChange={(e) => set({ ciudad: e.target.value })} placeholder="Curitiba" style={inputStyle} />
        </Campo>
        <Campo label="Aerolínea (opcional)">
          <input list="cotizador-aerolineas" value={op.aerolinea || ''} onChange={(e) => set({ aerolinea: e.target.value })} placeholder="LATAM" style={inputStyle} />
        </Campo>
        <Campo label="Escalas (0 = directo)">
          <NumInput value={op.escalas} onChange={(v) => set({ escalas: v === '' ? '' : String(Math.floor(Number(v) || 0)) })} />
        </Campo>
        <Campo label="Viaje aprox. (solo para ti)">
          <div style={{ ...inputStyle, display: 'flex', alignItems: 'center', background: 'var(--bg)', color: duracionVueloMin(op) > 20 * 60 ? 'var(--gold)' : 'var(--white)', fontWeight: 700 }}>
            {fmtDuracion(duracionVueloMin(op)) || '—'}
          </div>
        </Campo>
      </div>
      <Campo label="Etiqueta de este vuelo (sale en el orçamento)" style={{ marginBottom: 8 }}>
        <input
          value={op.tarjeta_ajustes?.etiqueta || ''} placeholder="vacío = automática"
          onChange={(e) => set({ tarjeta_ajustes: { ...(op.tarjeta_ajustes || {}), etiqueta: e.target.value } })} style={inputStyle}
        />
        <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
          {['Más económica', 'Más cómoda', 'La más rápida'].map((t) => (
            <button key={t} type="button" onClick={() => set({ tarjeta_ajustes: { ...(op.tarjeta_ajustes || {}), etiqueta: t } })} style={{ ...btnChico, padding: '5px 10px' }}>{t}</button>
          ))}
        </div>
      </Campo>
      {referencia && (
        <p style={{ fontSize: 11, lineHeight: 1.5, marginBottom: 8, color: referencia.gris ? 'var(--muted)' : 'var(--off-white)' }}>
          {referencia.texto}
        </p>
      )}

      <details style={{ marginBottom: 10 }}>
        <summary style={{ fontSize: 12, fontWeight: 700, color: 'var(--orange)', cursor: 'pointer', padding: '6px 0' }}>
          Datos para el orçamento Detallado (opcionales)
        </summary>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, margin: '8px 0' }}>
          <Campo label="Número de vuelo">
            <input
              value={op.numero_vuelo || ''} placeholder="G3 1234"
              onChange={(e) => {
                const aerolinea = !op.aerolinea ? aerolineaDeVuelo(e.target.value) : '';
                set({ numero_vuelo: e.target.value, ...(aerolinea ? { aerolinea } : {}) });
              }}
              style={inputStyle}
            />
          </Campo>
          <Campo label="Localizador (cuando ya compraste)"><input value={op.localizador || ''} onChange={(e) => set({ localizador: e.target.value })} style={inputStyle} /></Campo>
          <Campo label="Equipaje (vacío = sin equipaje despachado)" style={{ gridColumn: '1 / -1' }}>
            <input value={op.equipaje || ''} onChange={(e) => set({ equipaje: e.target.value })} placeholder="sin equipaje despachado" style={inputStyle} />
          </Campo>
          <label style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13, color: 'var(--white)', cursor: 'pointer' }}>
            <input type="checkbox" checked={!!op.traslado_incluido} onChange={(e) => set({ traslado_incluido: e.target.checked })} style={{ width: 18, height: 18, padding: 0, marginTop: 2 }} />
            <span>
              Incluyo el traslado del aeropuerto a la terminal (Uber)
              <span style={{ display: 'block', fontSize: 11, color: 'var(--muted)', marginTop: 2, lineHeight: 1.4 }}>
                Márcalo si tú pagas ese transporte (el monto va en "Otro extra"). En la imagen sale la línea "Traslado incluido".
              </span>
            </span>
          </label>
          {Number(op.otro_extra) > 0 && !op.traslado_incluido && (
            <p style={{ gridColumn: '1 / -1', fontSize: 11, color: 'var(--gold)', margin: 0, lineHeight: 1.4 }}>
              Tienes un "Otro extra" de R$ {fmtBRL(Number(op.otro_extra), 0)}. Si es el traslado, marca la casilla de arriba.
            </p>
          )}
          <details style={{ gridColumn: '1 / -1' }}>
            <summary style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', cursor: 'pointer', padding: '4px 0' }}>Más datos (aeropuertos y terminales; se deducen solos)</summary>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 6 }}>
              <Campo label="Aeropuerto de salida"><input value={op.aeropuerto_salida || ''} onChange={(e) => set({ aeropuerto_salida: e.target.value })} placeholder={aeropuertoSalidaDe(op)} style={inputStyle} /></Campo>
              <Campo label="Terminal de salida"><input value={op.terminal_salida || ''} onChange={(e) => set({ terminal_salida: e.target.value })} placeholder="si la sabes" style={inputStyle} /></Campo>
              <Campo label="Aeropuerto de llegada"><input value={op.aeropuerto_llegada || ''} onChange={(e) => set({ aeropuerto_llegada: e.target.value })} placeholder={aeropuertoDeCiudad(op.ciudad) || 'según la ciudad'} style={inputStyle} /></Campo>
              <Campo label="Terminal de llegada"><input value={op.terminal_llegada || ''} onChange={(e) => set({ terminal_llegada: e.target.value })} placeholder="si la sabes" style={inputStyle} /></Campo>
            </div>
          </details>
        </div>
      </details>

      <p className="section-label">PRECIO DEL VUELO (una o varias fuentes)</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
        <Campo label="Comprarviagem (lista)"><NumInput value={op.comprarviagem_lista} onChange={(v) => set({ comprarviagem_lista: v })} /></Campo>
        <Campo label="Speedmilhas (Total Pix)"><NumInput value={op.speedmilhas} onChange={(v) => set({ speedmilhas: v })} /></Campo>
        <Campo label="Resumen del pedido"><NumInput value={op.resumen_pedido} onChange={(v) => set({ resumen_pedido: v })} /></Campo>
        <Campo label="Millas (cantidad total)"><NumInput value={op.millas_cantidad} onChange={(v) => set({ millas_cantidad: v })} /></Campo>
        <Campo label="Milheiro R$"><NumInput value={op.milheiro} placeholder={`vacío = ${fmtBRL(ajustes.milheiro_defecto, 0)}`} onChange={(v) => set({ milheiro: v })} /></Campo>
        <Campo label="Taxa R$"><NumInput value={op.taxa} placeholder={`vacío = ${fmtBRL(ajustes.taxa_defecto)}`} onChange={(v) => set({ taxa: v })} /></Campo>
      </div>

      {esRev && (
        <>
          <p className="section-label">PRECIO AL REVENDEDOR</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
            <Campo label="Precio final R$">
              <NumInput value={op.precio_final_manual} placeholder={r.sugerido === null ? 'vacío = sugerido' : `vacío = ${fmtBRL(r.sugerido, 0)}`} onChange={(v) => set({ precio_final_manual: v })} />
            </Campo>
            <Campo label="Ganancia mínima de esta opción R$">
              <NumInput value={op.ganancia_minima_opcion} placeholder={`vacío = ${fmtBRL(r.gananciaMin, 0)}`} onChange={(v) => set({ ganancia_minima_opcion: v })} />
            </Campo>
          </div>
        </>
      )}

      {op.sale_de === 'BVB' ? (
        <p style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.5, margin: '0 0 8px' }}>
          Vuela directo desde Boa Vista: no hay ómnibus a Manaus ni espera allá.
          {r.noches !== null && r.noches !== undefined ? ` Hostal: ${r.noches} ${r.noches === 1 ? 'noche' : 'noches'}.` : ''}
        </p>
      ) : (
        <>
          <p className="section-label">ÓMNIBUS BOA VISTA → MANAUS</p>
          <Campo label="Total BRT (vacío = el de la tabla de horarios)" style={{ marginBottom: 8 }}>
            <NumInput value={op.omnibus_total_manual} placeholder={`vacío = ${fmtBRL(ajustes.omnibus_precio, 0)}`} onChange={(v) => set({ omnibus_total_manual: v })} />
          </Campo>
        </>
      )}

      <p className="section-label">OTRO DESTINO: ÓMNIBUS EXTRA</p>
      {extras.length === 0 && r.conexion.primerDesde !== null && (
        <p style={{ fontSize: 12, color: 'var(--gold)', marginBottom: 8 }}>
          Si hay ómnibus después del vuelo, búscalo desde {fmtHoraAbs(r.conexion.primerDesde)}.
        </p>
      )}
      {extras.length > 0 && r.conexion.llegadaVueloAbs === null && (
        <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>
          Escribe la hora de llegada del vuelo para saber si el ómnibus extra sirve.
        </p>
      )}
      {extras.map((ex, i) => (
        <ExtraOmnibus
          key={i}
          extra={ex}
          indice={i}
          tramo={r.conexion.tramos[i] || { ok: null, desde: null, esperaMin: null }}
          onChange={(nuevo) => setExtra(i, nuevo)}
          onBorrar={() => set({ extras: extras.filter((_, j) => j !== i) })}
        />
      ))}
      <button onClick={() => set({ extras: [...extras, { ciudad: '', total: '' }] })} style={{ ...btnChico, width: '100%', marginBottom: 8, color: 'var(--orange)', borderColor: 'var(--orange)' }}>
        + Agregar ómnibus extra
      </button>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
        <Campo label="Otro extra R$ (Uber, etc.)"><NumInput value={op.otro_extra} allowNegative onChange={(v) => set({ otro_extra: v })} /></Campo>
        <Campo label="Nota (aerolínea, horario)">
          <input value={op.nota || ''} onChange={(e) => set({ nota: e.target.value })} placeholder="Azul 02:35 → 13:10" style={inputStyle} />
        </Campo>
      </div>

      <p className="section-label" style={{ marginTop: 14 }}>DESGLOSE</p>
      <div style={{ background: 'var(--bg)', borderWidth: 1, borderStyle: 'solid', borderColor: 'var(--navy-border)', borderRadius: 10, padding: '8px 12px', marginBottom: 8 }}>
        <Linea k="Ómnibus elegido" v={r.omnibusEleg || (r.estado === 'sin_omnibus' ? 'Ninguno llega' : '—')} />
        {!esRev && <Linea k="Hostal (Boa Vista)" v={r.hostal === null ? '—' : `R$ ${fmtBRL(r.hostal)}`} />}
        {!r.saleBVB && (
          <>
            <Linea k="Ómnibus Total BRT" v={r.omnibusTotal === null ? '—' : `R$ ${fmtBRL(r.omnibusTotal)}`} />
            <Linea k="Tu comisión BRT" v={r.comision === null ? '—' : `R$ ${fmtBRL(r.comision)}`} color="var(--success)" />
            <Linea k="Ómnibus neto" v={r.omnibusNeto === null ? '—' : `R$ ${fmtBRL(r.omnibusNeto)}`} />
          </>
        )}
        {extras.length > 0 && (
          <>
            <Linea k="Comisión ómnibus extra" v={`R$ ${fmtBRL(r.extraComision)}`} color="var(--success)" />
          </>
        )}
        {(extras.length > 0 || op.otro_extra) && <Linea k="Extras neto (ómnibus + otro)" v={`R$ ${fmtBRL(r.extraNeto)}`} />}
        <div style={{ borderTop: '1px solid var(--navy-border)', margin: '6px 0' }} />
        {nombresFuentes.map((nombre, i) => (
          <Linea
            key={nombre}
            k={nombre}
            v={r.costos[i] === null ? '—' : `R$ ${fmtBRL(r.costos[i])}`}
            fuerte={i === mejorIdx}
            color={i === mejorIdx ? 'var(--success)' : undefined}
          />
        ))}
        <div style={{ borderTop: '1px solid var(--navy-border)', margin: '6px 0' }} />
        <Linea k="Mejor costo" v={r.mejorCosto === null ? '—' : `R$ ${fmtBRL(r.mejorCosto)}`} fuerte />
        <Linea k="Mejor fuente" v={r.mejorFuente || '—'} fuerte />
        {esRev ? (
          <>
            <Linea k="Costo real total" v={r.costoTotal === null ? '—' : `R$ ${fmtBRL(r.costoTotal)}`} fuerte />
            <Linea k="Precio sugerido" v={r.sugerido === null ? '—' : `R$ ${fmtBRL(r.sugerido)}`} />
            <Linea k="Precio final" v={r.precioFinal === null ? '—' : `R$ ${fmtBRL(r.precioFinal)}`} fuerte />
            <Linea k="Ganancia" v={r.ganancia === null ? '—' : `R$ ${fmtBRL(r.ganancia)} (${fmtBRL(r.gananciaPct * 100)}%)`} fuerte color={est?.color} />
          </>
        ) : (
          <Linea k="Ganancia" v={r.ganancia === null ? '—' : `R$ ${fmtBRL(r.ganancia, 0)}`} fuerte color={est?.color} />
        )}
        <Linea k="Estado" v={est ? est.texto : '—'} fuerte color={est?.color} />
      </div>

      <p className="section-label" style={{ marginTop: 14 }}>HASTA CUÁNTO PAGAR POR EL VUELO</p>
      <div style={{ background: 'var(--bg)', borderWidth: 1, borderStyle: 'solid', borderColor: 'var(--navy-border)', borderRadius: 10, padding: '8px 12px', marginBottom: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr 1fr', gap: 4, fontSize: 11, color: 'var(--muted)', fontWeight: 700, paddingBottom: 4 }}>
          <span>Para ganar</span><span style={{ textAlign: 'right' }}>Costo real máx.</span><span style={{ textAlign: 'right' }}>Lista Comprarviagem</span>
        </div>
        {r.topes.map((t) => (
          <div key={t.nivel} style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr 1fr', gap: 4, fontSize: 13, padding: '3px 0' }}>
            <span style={{ color: 'var(--orange)', fontWeight: 700 }}>R$ {fmtBRL(t.nivel, 0)}</span>
            <span style={{ textAlign: 'right', color: 'var(--white)' }}>{t.costoMax === null ? '—' : fmtBRL(t.costoMax, 0)}</span>
            <span style={{ textAlign: 'right', color: 'var(--white)' }}>{t.listaMax === null ? '—' : fmtBRL(t.listaMax, 0)}</span>
          </div>
        ))}
        {r.topes.every((t) => t.costoMax === null) && (
          <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
            {esRev ? 'Escribe la hora del vuelo y un precio final (o espera el sugerido) para calcular los topes.' : 'Escribe la hora del vuelo para calcular los topes.'}
          </p>
        )}
      </div>

      <button onClick={onBorrar} style={{ ...btnChico, width: '100%', color: 'var(--error)' }}>Quitar esta fila</button>
    </div>
  );
}

export default function CotizadorCotizacion({ id, ajustes, horarios, onBack }) {
  const [cot, setCot] = useState(null);
  const [abierto, setAbierto] = useState(null);
  const [imagen, setImagen] = useState(false);
  const [orcamento, setOrcamento] = useState(false);
  const [revendedores, setRevendedores] = useState([]);
  const [registros, setRegistros] = useState([]);
  const { estado, programar, flush } = useAutoGuardado();

  useEffect(() => {
    obtenerCotizacion(id).then(setCot).catch(() => toast('No se pudo abrir la cotización.', 'error'));
    listarRevendedores().then(setRevendedores);
  }, [id]);

  // Historial de precios para la línea de referencia y el autocompletar de ciudades.
  useEffect(() => {
    cargarRegistros(ajustes, horarios).then(setRegistros).catch(() => setRegistros([]));
  }, [ajustes, horarios]);

  const ciudades = useMemo(() => {
    const vistas = new Map();
    registros.forEach((r) => { const k = normalizarCiudad(r.destino); if (k && !vistas.has(k)) vistas.set(k, r.destino); });
    return [...vistas.values()].sort((a, b) => a.localeCompare(b, 'es'));
  }, [registros]);

  const resultados = useMemo(() => (
    cot ? cot.opciones.map((op) => calcularOpcion({ cotizacion: cot, opcion: op, ajustes, horarios })) : []
  ), [cot, ajustes, horarios]);
  const mejor = useMemo(() => mejorOpcion(resultados), [resultados]);

  if (!cot) return <p style={{ color: 'var(--muted)', textAlign: 'center', fontSize: 13 }}>Cargando…</p>;
  if (orcamento) {
    return (
      <CotizadorOrcamento
        cot={cot} ajustes={ajustes} horarios={horarios} resultados={resultados}
        onCambiarOpcion={(opId, patch) => setCot((c) => ({ ...c, opciones: c.opciones.map((o) => (o.id === opId ? { ...o, ...patch } : o)) }))}
        onCambiarCot={(patch) => setCot((c) => ({ ...c, ...patch }))}
        onBack={() => setOrcamento(false)}
      />
    );
  }
  if (imagen) return <CotizadorImagen cot={cot} ajustes={ajustes} horarios={horarios} resultados={resultados} onBack={() => setImagen(false)} />;

  const setCab = (patch) => {
    setCot((c) => ({ ...c, ...patch }));
    // Mientras se escribe ("4," o vacío) solo se guarda lo que ya es número.
    const db = { ...patch };
    ['usd', 'tasa'].forEach((k) => { if (k in db && (db[k] === '' || Number.isNaN(Number(db[k])))) delete db[k]; });
    if (Object.keys(db).length) programar('cot', db, (p) => actualizarCotizacion(id, p));
  };

  // Al cambiar el día de llegada, todas las filas se corren los mismos días
  // (las que cambiaste a mano conservan su diferencia).
  const cambiarLlegada = (nueva) => {
    const ant = diaNum(cot.llegada);
    const nuevo = diaNum(nueva);
    if (!nueva || nuevo === null) return setCab({ llegada: nueva });
    const delta = nuevo - (ant ?? nuevo);
    const fechas = cot.opciones.map((o) => (delta ? sumarDias(o.dia_vuelo, delta) : o.dia_vuelo));
    setCot((c) => ({ ...c, llegada: nueva, opciones: c.opciones.map((o, i) => ({ ...o, dia_vuelo: fechas[i] })) }));
    programar('cot', { llegada: nueva }, (p) => actualizarCotizacion(id, p));
    if (delta) programar('fechas', {}, () => moverFilasPorLlegada(cot.opciones, fechas));
  };

  const esRev = cot.tipo === 'revendedor';

  const cambiarTipo = (tipo) => { if (tipo !== cot.tipo) setCab({ tipo }); };
  const elegirRevendedor = (rid) => {
    const r = revendedores.find((x) => x.id === rid) || null;
    setCab({ revendedor_id: rid || null, revendedor: r });
  };
  const nuevoRevendedor = async () => {
    const nombre = (window.prompt('Nombre del revendedor') || '').trim();
    if (!nombre) return;
    try {
      const r = await crearRevendedor(nombre);
      setRevendedores((l) => [...l, r]);
      elegirRevendedor(r.id);
      setCot((c) => ({ ...c, revendedor: r }));
    } catch { toast('No se pudo crear el revendedor.', 'error'); }
  };
  // Los valores del revendedor se guardan en su ficha: valen para todos sus clientes.
  const editarRev = (patch) => {
    const rev = cot.revendedor;
    if (!rev) return;
    setCot((c) => ({ ...c, revendedor: { ...c.revendedor, ...patch } }));
    setRevendedores((l) => l.map((x) => (x.id === rev.id ? { ...x, ...patch } : x)));
    const db = {};
    Object.entries(patch).forEach(([k, v]) => { if (v !== '' && !Number.isNaN(Number(v))) db[k] = Number(v); });
    if (Object.keys(db).length) programar(`rev:${rev.id}`, db, (p) => actualizarRevendedor(rev.id, p));
  };

  const setOp = (opId, patch) => {
    setCot((c) => ({ ...c, opciones: c.opciones.map((o) => (o.id === opId ? { ...o, ...patch } : o)) }));
    programar(`op:${opId}`, patch, (p) => actualizarOpcion(opId, p));
  };

  const agregar = async () => {
    const ultimo = cot.opciones[cot.opciones.length - 1];
    const dia = ultimo?.dia_vuelo ? sumarDias(ultimo.dia_vuelo, 1) : sumarDias(cot.llegada, 2);
    try {
      const nueva = await agregarOpcion(id, cot.opciones.length, dia);
      setCot((c) => ({ ...c, opciones: [...c.opciones, nueva] }));
      setAbierto(nueva.id);
    } catch { toast('No se pudo agregar la opción.', 'error'); }
  };

  const duplicar = async (op) => {
    try {
      await flush(); // que la copia salga de lo último que escribiste
      const nueva = await duplicarOpcion(id, op, cot.opciones);
      setCot((c) => {
        const i = c.opciones.findIndex((o) => o.id === op.id);
        return { ...c, opciones: [...c.opciones.slice(0, i + 1), nueva, ...c.opciones.slice(i + 1)] };
      });
      setAbierto(nueva.id);
      toast('Fila duplicada: pon la hora, aerolínea y precio del otro vuelo.');
    } catch { toast('No se pudo duplicar la fila.', 'error'); }
  };

  // Llenar con capturas: el primer vuelo va a esta fila si está vacía; los demás, a filas nuevas debajo.
  const aplicarCapturas = async (op, plan) => {
    try {
      await flush();
      const unirNota = (p) => ({ ...p, ...(p.nota ? { nota: [op.nota, p.nota].filter((x, i, a) => x && a.indexOf(x) === i).join(' · ') } : {}) });
      const actual = plan.find((p) => p.enActual);
      if (actual) setOp(op.id, unirNota(actual.patch));
      const nuevas = plan.filter((p) => !p.enActual);
      if (nuevas.length) {
        const creadas = await crearOpcionesDespues(id, op, cot.opciones, nuevas.map((p) => p.patch));
        setCot((c) => {
          const i = c.opciones.findIndex((o) => o.id === op.id);
          return { ...c, opciones: [...c.opciones.slice(0, i + 1), ...creadas, ...c.opciones.slice(i + 1)] };
        });
      }
      toast(`Listo: ${plan.length} ${plan.length === 1 ? 'vuelo cargado' : 'vuelos cargados'}. Revisa los datos.`);
    } catch { toast('No se pudieron cargar los vuelos.', 'error'); }
  };

  const quitar = async (opId) => {
    if (!window.confirm('¿Quitar esta fila?')) return;
    try {
      await borrarOpcion(opId);
      setCot((c) => ({ ...c, opciones: c.opciones.filter((o) => o.id !== opId) }));
      setAbierto(null);
    } catch { toast('No se pudo quitar.', 'error'); }
  };

  const R = cot.usd !== '' && cot.tasa !== '' ? Number(cot.usd) * Number(cot.tasa) : null;
  const rMejor = mejor >= 0 ? resultados[mejor] : null;
  const opMejor = mejor >= 0 ? cot.opciones[mejor] : null;
  const estMejor = rMejor?.estado ? ESTADOS[rMejor.estado] : null;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <button onClick={async () => { await flush(); onBack(); }} style={btnChico}>← Cotizaciones</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <IndicadorGuardado estado={estado} />
          <button onClick={async () => { await flush(); setOrcamento(true); }} style={{ ...btnChico, color: 'var(--orange)', borderColor: 'var(--orange)', fontWeight: 700 }}>Crear orçamento</button>
          {!esRev && (
            <button onClick={async () => { await flush(); setImagen(true); }} style={{ ...btnChico, color: 'var(--orange)', borderColor: 'var(--orange)', fontWeight: 700 }}>Itinerario para el cliente</button>
          )}
        </div>
      </div>

      <div style={cardStyle}>
        <Campo label="Nombre del cliente" style={{ marginBottom: 10 }}>
          <input value={cot.nombre} onChange={(e) => setCab({ nombre: e.target.value })} placeholder="BV Porto Alegre Marta" style={inputStyle} />
        </Campo>
        <Campo label="Tipo de cliente" style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            {[['directo', 'Cliente directo'], ['revendedor', 'Revendedor']].map(([t, texto]) => (
              <button key={t} onClick={() => cambiarTipo(t)} style={{
                ...btnChico, flex: 1, fontWeight: 700,
                color: cot.tipo === t ? 'var(--orange)' : 'var(--muted)',
                borderColor: cot.tipo === t ? 'var(--orange)' : 'var(--navy-border)',
              }}>{texto}</button>
            ))}
          </div>
        </Campo>
        {esRev ? (
          <>
            <Campo label="Revendedor" style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', gap: 6 }}>
                <select value={cot.revendedor_id || ''} onChange={(e) => elegirRevendedor(e.target.value)} style={{ ...inputStyle, flex: 1 }}>
                  <option value="">— Elegir —</option>
                  {revendedores.map((r) => <option key={r.id} value={r.id}>{r.nombre || '(sin nombre)'}</option>)}
                </select>
                <button onClick={nuevoRevendedor} style={{ ...btnChico, whiteSpace: 'nowrap', color: 'var(--orange)', borderColor: 'var(--orange)' }}>+ Nuevo</button>
              </div>
            </Campo>
            {cot.revendedor ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                <Campo label="Margen % (sobre la venta)"><NumInput value={cot.revendedor.margen_pct} onChange={(v) => editarRev({ margen_pct: v })} /></Campo>
                <Campo label="Ganancia mín. R$"><NumInput value={cot.revendedor.ganancia_minima} onChange={(v) => editarRev({ ganancia_minima: v })} /></Campo>
                <Campo label="Redondeo R$"><NumInput value={cot.revendedor.redondeo} onChange={(v) => editarRev({ redondeo: v })} /></Campo>
              </div>
            ) : (
              <p style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.5 }}>
                Elige un revendedor o crea uno nuevo. Mientras tanto se usa margen 7%, ganancia mínima 0 y redondeo 1.
              </p>
            )}
            <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 8, lineHeight: 1.5 }}>
              Sin hostal: el revendedor se ocupa de que su pasajero llegue a Boa Vista. Los valores se guardan en la ficha del revendedor.
            </p>
          </>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
              <Campo label="Día que llegó a Boa Vista">
                <input type="date" value={cot.llegada || ''} onChange={(e) => cambiarLlegada(e.target.value)} style={inputStyle} />
              </Campo>
              <Campo label="Precio al cliente (US$)"><NumInput value={cot.usd} onChange={(v) => setCab({ usd: v })} /></Campo>
              <Campo label="Tasa de cambio"><NumInput value={cot.tasa} onChange={(v) => setCab({ tasa: v })} /></Campo>
              <Campo label="Ganancia mínima R$"><NumInput value={cot.ganancia_minima} placeholder={`vacío = ${fmtBRL(ajustes.ganancia_minima, 0)}`} onChange={(v) => setCab({ ganancia_minima: v })} /></Campo>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', background: 'var(--bg)', borderWidth: 1, borderStyle: 'solid', borderColor: 'var(--navy-border)', borderRadius: 10, padding: '10px 12px' }}>
              <span style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 700 }}>REALES QUE RECIBES</span>
              <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--white)' }}>{R === null ? '—' : `R$ ${fmtBRL(R)}`}</span>
            </div>
          </>
        )}
      </div>

      <div style={{ ...cardStyle, borderColor: estMejor ? estMejor.color : 'var(--navy-border)' }}>
        <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: 'var(--muted)', marginBottom: 4 }}>MEJOR OPCIÓN</p>
        {rMejor ? (
          <p style={{ fontSize: 15, fontWeight: 800, color: estMejor?.color || 'var(--white)', lineHeight: 1.4 }}>
            {fmtFecha(opMejor.dia_vuelo)} · {opMejor.ciudad ? `${opMejor.ciudad} · ` : ''}
            {esRev
              ? `Precio R$ ${fmtBRL(rMejor.precioFinal)} · Ganancia R$ ${fmtBRL(rMejor.ganancia)} (${fmtBRL(rMejor.gananciaPct * 100)}%) · ${estMejor?.texto}`
              : `R$ ${fmtBRL(rMejor.ganancia, 0)} · ${estMejor?.texto}`}
          </p>
        ) : (
          <p style={{ fontSize: 13, color: 'var(--muted)' }}>Escribe la hora y un precio de vuelo para ver cuál conviene.</p>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '64px 50px 1fr 1fr 62px', gap: 4, padding: '0 10px 6px', fontSize: 10, fontWeight: 700, color: 'var(--muted)' }}>
        <span>DÍA</span><span>HORA</span><span style={{ textAlign: 'right' }}>{esRev ? 'PRECIO' : 'MEJOR COSTO'}</span><span style={{ textAlign: 'right' }}>GANANCIA</span><span style={{ textAlign: 'right' }}>ESTADO</span>
      </div>
      {cot.opciones.map((op, i) => {
        const r = resultados[i];
        const est = r.estado ? ESTADOS[r.estado] : null;
        const abiertoAhora = abierto === op.id;
        return (
          <div key={op.id} style={{ background: 'var(--navy-card)', borderWidth: 1, borderStyle: 'solid', borderColor: abiertoAhora ? 'var(--orange)' : 'var(--navy-border)', borderLeftWidth: 4, borderLeftColor: est?.color || 'var(--navy-border)', borderRadius: 12, marginBottom: 8, padding: '10px 10px' }}>
            <div onClick={() => setAbierto(abiertoAhora ? null : op.id)} style={{ display: 'grid', gridTemplateColumns: '64px 50px 1fr 1fr 62px', gap: 4, alignItems: 'center', cursor: 'pointer', fontSize: 13 }}>
              <span style={{ fontWeight: 700, color: 'var(--white)' }}>
                {fmtFecha(op.dia_vuelo)} <span style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 600 }}>{diaSemana(op.dia_vuelo)}</span>
              </span>
              <span style={{ color: op.hora_vuelo ? 'var(--white)' : 'var(--muted)' }}>{op.hora_vuelo || '—'}</span>
              <span style={{ textAlign: 'right', color: 'var(--off-white)' }}>
                {esRev ? (r.precioFinal === null ? '—' : fmtBRL(r.precioFinal)) : (r.mejorCosto === null ? '—' : fmtBRL(r.mejorCosto))}
              </span>
              <span style={{ textAlign: 'right', fontWeight: 800, color: est?.color || 'var(--muted)' }}>{r.ganancia === null ? '—' : fmtBRL(r.ganancia, esRev ? 2 : 0)}</span>
              <span style={{ textAlign: 'right', fontSize: 11, fontWeight: 700, color: est?.color || 'var(--muted)' }}>{est?.corto || ''}</span>
            </div>
            {!abiertoAhora && resumenFila(op) && (
              <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>{resumenFila(op)}</p>
            )}
            {abiertoAhora && (
              <Detalle op={op} r={r} ajustes={ajustes} referencia={lineaReferencia(registros.filter((x) => x.opcionId !== op.id), op.ciudad, hoyIso(), ajustes.ventana_historial_dias || 21)} onChange={(patch) => setOp(op.id, patch)} onBorrar={() => quitar(op.id)} onDuplicar={() => duplicar(op)} onCapturas={(plan) => aplicarCapturas(op, plan)} />
            )}
          </div>
        );
      })}

      <datalist id="cotizador-aerolineas">
        {AEROLINEAS_COMUNES.map((a) => <option key={a} value={a} />)}
      </datalist>
      <datalist id="cotizador-ciudades">
        {ciudades.map((c) => <option key={c} value={c} />)}
      </datalist>

      <button onClick={agregar} style={{ ...btnChico, width: '100%', color: 'var(--orange)', borderColor: 'var(--orange)', padding: '12px 0', fontSize: 13 }}>
        + Agregar opción
      </button>
    </div>
  );
}
